import {errorDetail} from '../../helper/errorDetail';

const EXPORT_WARNING_CODE_HEADER = 'X-Alphasearch-Export-Warning-Code';
const EXPORT_WARNING_MESSAGE_HEADER = 'X-Alphasearch-Export-Warning-Message';

function firstPresent(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function responseHeader(headers, name) {
  if (!headers || !name) return undefined;
  if (typeof headers.get === 'function') {
    return headers.get(name) || headers.get(name.toLowerCase());
  }

  const lowerName = name.toLowerCase();
  const headerEntry = Object.entries(headers).find(([key]) => key.toLowerCase() === lowerName);
  return headerEntry?.[1];
}

export function exportWarningFromResponse(response) {
  const message = responseHeader(response?.headers, EXPORT_WARNING_MESSAGE_HEADER);
  if (!message) return null;
  return {
    code: responseHeader(response?.headers, EXPORT_WARNING_CODE_HEADER) || null,
    message,
  };
}

export function normalizeExportWarnings(...values) {
  return values
    .flat()
    .filter(Boolean)
    .map((warning) => {
      if (typeof warning === 'string') return { code: null, message: warning };
      return {
        code: warning.code || null,
        message: errorDetail(warning, ''),
      };
    })
    .filter((warning) => warning.message);
}

export function normalizeExportCountKind(value, count) {
  const normalized = String(value || '').toLowerCase();
  if (['known', 'exact'].includes(normalized)) return 'known';
  if (['estimated', 'estimate', 'approximate'].includes(normalized)) return 'estimated';
  return Number.isFinite(count) ? 'known' : 'unavailable';
}

function isBlob(value) {
  return typeof Blob !== 'undefined' && value instanceof Blob;
}

function readBlobText(blob) {
  if (typeof blob.text === 'function') return blob.text();
  if (typeof FileReader === 'undefined') return Promise.resolve('');

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Unable to read export response.'));
    reader.readAsText(blob);
  });
}

export async function exportFailurePayload(response) {
  const data = response?.data;
  if (!isBlob(data)) {
    return { data, parsedBlob: false };
  }

  const text = await readBlobText(data);
  if (!text.trim()) {
    return { data: null, parsedBlob: true };
  }

  try {
    return { data: JSON.parse(text), parsedBlob: true };
  } catch (_error) {
    return { data: { message: text }, parsedBlob: true };
  }
}

export function exportDetail(payload) {
  const detail = payload?.detail;
  return detail && typeof detail === 'object' ? detail : payload;
}

export function isExpiredArtifactDetail(detail) {
  return Boolean(
    detail &&
      typeof detail === 'object' &&
      (
        detail.code === 'artifact_expired' ||
        detail.artifact_state === 'expired' ||
        detail.artifact?.artifact_state === 'expired'
      )
  );
}

export function expiredArtifactData(detail, artifactId) {
  const artifact = detail?.artifact && typeof detail.artifact === 'object' ? detail.artifact : {};
  const tombstone = detail?.artifact_tombstone && typeof detail.artifact_tombstone === 'object'
    ? detail.artifact_tombstone
    : artifact.artifact_tombstone;
  const message = firstPresent(
    detail?.message,
    artifact.empty_message,
    artifact.message,
    tombstone?.message,
    'This saved target list has expired. Run discovery again to create a fresh list.'
  );
  const notices = Array.isArray(artifact.notices) && artifact.notices.length > 0
    ? artifact.notices
    : [{ message }];
  const sourceActions = Array.isArray(artifact.actions) ? artifact.actions : [];
  const hasExportAction = sourceActions.some((action) =>
    action?.key === 'export_csv' || action?.kind === 'export'
  );
  const actions = hasExportAction
    ? sourceActions
    : [
        {
          key: 'export_csv',
          label: 'Export',
          kind: 'export',
          enabled: false,
          disabled_reason: message,
        },
        ...sourceActions,
      ];

  return {
    artifact: {
      ...artifact,
      actions,
      artifact_id: firstPresent(
        artifact.artifact_id,
        artifact.id,
        detail?.artifact_id,
        tombstone?.artifact_id,
        artifactId
      ),
      artifact_state: firstPresent(artifact.artifact_state, detail?.artifact_state, 'expired'),
      total_count: firstPresent(artifact.total_count, artifact.candidate_count, 0),
      empty_message: firstPresent(artifact.empty_message, message),
      notices,
    },
    candidates: [],
  };
}
